import { z } from 'zod';
import { inquiryAddressSchema } from './address.js';
import { buildingTypeSchema } from './building-type.js';
import { inquiryStatusSchema } from './status.js';
import { clientCategorySchema, clientFamilySchema } from '../client/client.js';
import { inspectionProgressSchema } from '../inspection/inspection-step.js';
import { inspectionResultSchema } from '../inspection/result.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * Une ligne du « Tableau des demandes », telle que l'API la sert au back-office. L'adresse est
 * aplatie et nullable pour les mêmes raisons que celle de la tournée — voir
 * `assignedInquirySchema`.
 *
 * L'équipe porte son `id` **et** son nom : le nom est ce que la colonne affiche (`JL·TB`,
 * verbatim — M22), l'`id` ce sur quoi le filtre porte. Sans l'`id`, filtrer par équipe
 * reviendrait à comparer des libellés.
 *
 * ⚠️ **Ce que cette ligne ne porte pas, l'API ne le sert pas encore** — et la raison diffère
 * d'une colonne à l'autre, ce qui dit laquelle peut arriver la première. Le type de demande, le
 * client et sa nature, et le marché sont des colonnes du prototype validé, et l'inventaire de
 * M14 dit par quoi les servir :
 *
 * - le **client** et sa **nature** ont désormais leurs tables — `Inquiry.client_id` depuis M32,
 *   `Site.lot_id → Lot.client_id` pour la campagne, et `Client.category` —, et le **marché** sa
 *   colonne depuis M31 (`Municipality.sector_id`) ;
 * - le **type** (`Inquiry.purpose`) a lui aussi sa colonne depuis M32.
 *
 * Ce qui manque à tous les quatre est donc la même chose : la jointure côté API et la colonne
 * ici.
 *
 * Le **rendez-vous**, lui, en est sorti : `appointmentAt` est servi depuis #194.
 *
 * Les déclarer ici pour les servir en `null` constant ferait croire à un câblage ; chacune entre
 * quand le serveur la sert.
 */
export const inquiryListRowSchema = z.object({
    /** Le rang de contre-visite — 1, 2… —, `null` pour un premier contrôle (#321, Q5). */
    followUpRank: z.number().int().positive().nullable().default(null),
    id: uuidV7Schema,
    reference: z.string().nullable(),
    caseReference: z.string().nullable(),
    /** Le lien vers le dossier client (#410), pour « Gérer une demande ». */
    caseUrl: z.string().nullable(),
    status: inquiryStatusSchema,
    address: inquiryAddressSchema,
    team: z
        .object({
        id: uuidV7Schema,
        name: z.string(),
    })
        .nullable(),
    /**
     * Le créneau du **dernier** rendez-vous posé, ou `null` — la colonne « Rendez-vous » du
     * prototype, enfin servie.
     *
     * ⚠️ **Le créneau seul, pas le contact.** La tournée descend les deux, aplatis
     * (`assignedInquirySchema`), parce que l'agent sonne à la porte ; le tableau du bureau, lui,
     * n'affiche qu'une date. Transporter le contact jusqu'au navigateur pour l'y cacher serait de
     * la donnée personnelle promenée sans usage.
     *
     * ⚠️ **Le dernier, pas le premier** : un report en pose un second (M24), et c'est celui-là que
     * l'usager attend. Même règle que `lastAppointmentAt` sur une ligne de relance — les trois
     * lectures tapent la même table, elles ne doivent pas en tirer trois réponses.
     */
    appointmentAt: z.iso.datetime({ offset: true }).nullable(),
    /**
     * La **dernière saisie transmise** de la demande, ou `null` — le jeton que le bouton « Valider
     * terrain » de la file « Contrôles à valider » renvoie au serveur.
     *
     * ⚠️ **C'est ce champ qui remet le geste sur la ligne.** Valider exige `proposalId`
     * (`validateInspectionSchema`), et une ligne de tableau n'en portait aucune : le bouton avait
     * donc dû quitter la liste pour la fiche de lecture. Le prototype validé, lui, en pose deux sur
     * chaque ligne — « Modifier » et « Valider terrain » —, et c'est ce que le développeur a
     * retenu le 21/09.
     *
     * ⚠️ **La plus récente, et triée exactement comme la décision la trie** : `submitted_at`
     * décroissant, départagé par l'`id` croissant. Deux tablettes qui closent à la même seconde
     * portent le même instant, et un tri qui divergerait d'un bout à l'autre ferait refuser en
     * `proposal_superseded` un jeton parfaitement à jour — le valideur n'aurait alors aucune sortie.
     *
     * ⚠️ **Ce n'est pas la promesse que le contrôle est validable.** Le jeton désigne une saisie, il
     * ne dit ni que la demande est `submitted`, ni que la mention du signataire est cochée : le
     * serveur garde le dernier mot et refuse en `409` nommé. Une ligne qui le porte n'est donc pas
     * une ligne qui a un bouton — c'est `status` qui le dit.
     *
     * ⚠️ **Nullable, et `null` sur la plupart des lignes** : cinq autres écrans lisent la même
     * route, et une demande qu'aucune tablette n'a close n'a rien transmis. C'est le cas courant,
     * pas une anomalie.
     */
    /**
     * Le **type de contrôle** — la pastille que le prototype pose en tête d'une carte du rail
     * « À planifier », et la colonne « Type » des Relances et de la file d'export.
     *
     * Le **code**, pas le libellé : la colonne est du texte libre (M2, M10), et
     * `inquiryPurposeLabel` porte le repli sur la valeur brute qu'un type ajouté par GHP demande.
     */
    purpose: z.string().nullable(),
    proposalId: uuidV7Schema.nullable(),
    /**
     * Le **résumé** de cette saisie — conclusion et nombre de photos, les deux colonnes que la
     * maquette pose entre « Ville » et « Équipe » sur la file de validation.
     *
     * ⚠️ **Un objet nullable et non deux champs nullables**, comme sur la file d'export : les deux
     * valeurs sont absentes **ensemble** et pour une seule raison — aucune saisie, ou une charge
     * utile que le serveur ne relit plus (M21). Deux `null` indépendants laisseraient croire à deux
     * manques distincts.
     *
     * ⚠️ **Et `proposalId` reste à plat, à côté.** Il n'appartient pas à ce résumé : c'est le jeton
     * de concurrence du bouton « Valider terrain », et il doit survivre à une charge utile
     * illisible — le bureau garde le droit d'aller voir une fiche qu'il ne peut pas résumer.
     *
     * ⚠️ La conclusion est celle de M19 : la **surcharge de l'agent** quand il en a posé une, la
     * dérivation sinon. Elle sort de la proposition et non d'`Inspection`, qui n'existe qu'après
     * validation — or cette file montre justement ce qui n'est pas encore validé.
     */
    proposal: z
        .object({
        result: inspectionResultSchema,
        photoCount: z.number().int().min(0),
    })
        .nullable(),
    /**
     * L'instant du **dernier passage encore à traiter**, ou `null` — ce que la colonne « État »
     * affiche quand un contrôle n'a pas pu être honoré : un agent s'est déplacé, personne n'a
     * ouvert, et le dossier attend un courrier.
     *
     * ⚠️ **Ce n'est pas une valeur d'`Inquiry.status`, et ça ne doit pas le devenir.** Le cycle de
     * vie de M3 ne connaît pas cet état, et l'y ajouter toucherait la machine à états de tout le
     * produit pour un affichage. C'est un **fait daté à côté**, comme la livraison client (M26) :
     * la demande garde son statut, l'écran compose ce qu'il montre.
     *
     * ⚠️ **Une date et non un booléen**, alors que `null` suffirait à dire « aucun avis ». Un
     * gestionnaire qui lit « Avis de passage » demande aussitôt *depuis quand* — c'est ce qui dit si
     * le courrier traîne. Et l'horodatage **est** le constat (`passageNoticeSchema`) : c'est lui que
     * le recommandé cite.
     *
     * ⚠️ **Ni le motif ni l'état de l'avis**, que la liste « Avis de passage » sert déjà
     * (`passageNoticeRowSchema`) : ils choisissent le courrier, pas le libellé d'une colonne. Les
     * transporter sur chaque ligne des six écrans qui lisent cette route servirait quatre champs
     * pour une pastille.
     *
     * ⚠️ **Un avis traité n'en est plus un.** Le champ ne porte que les avis encore ouverts
     * (`PASSAGE_NOTICE_OPEN_STATUSES`) — une demande dont le bureau a soldé l'avis a repris son
     * cours, et l'annoncer pour toujours ferait mentir la colonne.
     */
    passageNoticedAt: z.iso.datetime({ offset: true }).nullable(),
    /**
     * **La dernière étape du contrôle** — commencé, signé, transmis (M42), ou `null` avant toute
     * étape. Ce que le bureau voit d'un contrôle encore sur la tablette, sans rien de la saisie.
     *
     * ⚠️ `default(null)` : une ligne servie avant ce champ se relit sans lui.
     */
    progress: inspectionProgressSchema.nullable().default(null),
    /**
     * Le **donneur d'ordre** de la demande, ou `null` — la colonne « Client » du prototype, qui
     * affichait « — » sur toutes les lignes faute d'être servie.
     *
     * ⚠️ **Deux chemins y mènent, et l'un gagne** : `Inquiry.client_id` (M32) l'emporte sur
     * `Site.lot_id → Lot.client_id` (M14). Le premier est le client de **cette demande** — une
     * vente arrivée hors campagne, le cas courant ; le second celui de la campagne dont le lieu
     * fait partie. Quand les deux existent, c'est la demande qui dit vrai.
     *
     * ⚠️ **La catégorie voyage avec le nom**, mais aucun écran ne la lit depuis que « Client » a
     * perdu sa pastille de nature (tableau « Pilotage GHP », 23/10/2026) : le filtre la partitionne
     * en base (`categoriesInFamily`). Un écran qui la reprendrait passe par `familyOf`, jamais par
     * une seconde partition.
     */
    client: z
        .object({
        id: uuidV7Schema,
        name: z.string(),
        category: clientCategorySchema,
    })
        .nullable(),
    /**
     * La **note pour l'équipe** de la demande, ou `null` — `Inquiry.comment` (M32), le texte libre
     * d'accès au site que la boîte « Attribuer & planifier » recueille : « Portail fermé, sonner
     * chez le voisin au n°25. », « Chien dans le jardin, prévenir avant passage. »
     *
     * ⚠️ **Sur la ligne, alors qu'aucune colonne ne l'affiche** : la boîte « Gérer » s'ouvre depuis
     * la ligne et remet la note dans son champ. Aller la chercher à l'ouverture ferait attendre la
     * boîte, et un enregistrement fait avant la réponse effacerait la note.
     *
     * ⚠️ Le bouton de copie de la colonne « Réf. client » la copiait (prototype validé) ; il copie la
     * référence depuis la recette du 13-14/10 (#439).
     */
    comment: z.string().nullable(),
    /**
     * Le type de bâti du lieu (#586, `Site.building_type`, M10) — Maison, Immeuble… `null` quand
     * aucun import ni personne ne l'a donné ; une réponse d'avant ce champ le rend `null` aussi.
     */
    buildingType: buildingTypeSchema.nullable().default(null),
    /** La date de la demande (#435), pour l'export. */
    requestedOn: z.iso.date().nullable().default(null),
    /**
     * La **personne à contacter** — le contact du rendez-vous, sinon le demandeur, comme sur la
     * pré-fiche de la tablette (#447). Le planning en montre le nom et le téléphone.
     */
    contact: z
        .object({
        lastName: z.string().nullable(),
        firstName: z.string().nullable(),
        phone: z.string().nullable(),
    })
        .nullable()
        .default(null),
});
/**
 * Les colonnes sur lesquelles le tableau se trie. Le tri est **une clause `ORDER BY`**, pas un
 * `sort()` sur la page reçue (D24) : trier une page de cinquante lignes sur six mille donnerait
 * un ordre juste localement et faux globalement.
 *
 * L'énumération est fermée pour une raison de sécurité autant que de justesse : une colonne de
 * tri venue de l'URL et concaténée dans la requête est une injection. Ici la valeur ne peut être
 * que l'une des sept, et le dépôt la traduit en expression Drizzle.
 *
 * Six d'entre elles sont les **six en-têtes cliquables** du prototype validé, dans l'ordre où le
 * tableau les affiche. `createdAt` est la septième et n'a pas d'en-tête : c'est l'ordre d'ouverture
 * de l'écran, celui qu'on retrouve en désélectionnant une colonne.
 *
 * ⚠️ **`createdAt` est la colonne par défaut, décroissante**, et c'est un choix de M21 plutôt
 * qu'une préférence : la Réf. GHP est **facultative** (M1), une demande créée à la main ou lue
 * d'un PDF de portail n'en porte aucune. Trier par référence envoyait donc en queue de liste
 * exactement les demandes qui viennent d'arriver et qui appellent une action. La date de
 * création, elle, n'est jamais nulle.
 *
 * ⚠️ **`client` trie sur une valeur que la ligne ne porte pas encore** : le nom du donneur
 * d'ordre s'atteint par `Site.lot_id → Lot.client_id`, et `inquiryListRowSchema` ne le sert pas.
 * Un tri n'est pas une colonne de résultat — il n'a besoin que d'une expression en base —, et
 * c'est ce qui le laisse arriver avant que la colonne « Client » soit remplie.
 */
export const INQUIRY_LIST_SORTS = [
    'createdAt',
    'reference',
    'address',
    'client',
    'team',
    'appointment',
    'status',
];
export const inquiryListSortSchema = z.enum(INQUIRY_LIST_SORTS);
export const INQUIRY_LIST_DIRECTIONS = ['asc', 'desc'];
export const inquiryListDirectionSchema = z.enum(INQUIRY_LIST_DIRECTIONS);
/**
 * Le filtre « Attribution » du prototype : toutes / attribuées / non attribuées. C'est
 * `Inquiry.team_id IS NOT NULL` et rien d'autre (M14) — pas un statut, pas un état maintenu à la
 * main.
 */
export const INQUIRY_LIST_ASSIGNMENTS = ['all', 'assigned', 'unassigned'];
export const inquiryListAssignmentSchema = z.enum(INQUIRY_LIST_ASSIGNMENTS);
/**
 * La **charge d'une équipe** comme filtre de liste : ce qu'elle a devant elle, ce qu'elle a
 * transmis, ou les deux. C'est le pendant paginé des deux compteurs de `TeamLoad` — la fiche
 * équipe montre les mêmes ensembles de statuts, mais en lignes plutôt qu'en nombres.
 *
 * ⚠️ **Un filtre nommé plutôt qu'un `status` multiple, et c'est le point.** Chacune des deux
 * valeurs recouvre **plusieurs** statuts de M3, et ce découpage est déjà écrit une fois —
 * `TEAM_LOAD_PENDING_STATUSES` / `TEAM_LOAD_DONE_STATUSES`, avec un spec qui refuse qu'un statut
 * de M3 en sorte. Une URL qui énumérerait les statuts en serait une **seconde** écriture, que
 * rien ne garderait d'accord avec la première : l'écran demande « à faire », le serveur seul sait
 * ce que ça recouvre. C'est `statusesForLoad` qui fait la traduction, et nulle part ailleurs.
 *
 * ⚠️ **Cumulé avec `status`, il le restreint sans le contredire** — les filtres se combinent en
 * `ET` comme partout ailleurs (M14). `load=pending&status=archived` ne rend donc rien : c'est
 * juste, et aucun écran ne l'écrit.
 */
export const INQUIRY_LIST_LOADS = ['all', 'pending', 'done'];
/** Le filtre « une étape est remontée de la tablette » (M42). */
export const INQUIRY_LIST_PROGRESSES = ['all', 'started'];
export const inquiryListLoadSchema = z.enum(INQUIRY_LIST_LOADS);
/**
 * Les onglets « À planifier » / « Avec RDV » du prototype : une demande a-t-elle un rendez-vous ?
 *
 * ⚠️ **C'est `EXISTS (SELECT 1 FROM appointment …)`, et rien d'autre** (M14) — pas un statut, pas
 * un état maintenu à la main. `Appointment` ne porte aucun `status` (M24), et une demande
 * « planifiée » n'est que le fait qu'une ligne existe.
 *
 * ⚠️ **`scheduled` ne dit pas que le rendez-vous est à venir.** Un créneau passé sans contrôle
 * transmis est un rendez-vous manqué, pas une demande à planifier : c'est l'écran « Relances »
 * qui le relève, avec sa borne temporelle à lui (`REMINDER_MISSED_APPOINTMENT_STATUSES`).
 *
 * ⚠️ **`unscheduled` et `to_plan` ne sont pas la même question, et les deux servent.**
 * `unscheduled` est le **prédicat pur** — aucune ligne dans `appointment` —, et c'est lui que les
 * compteurs partitionnent avec `scheduled`. `to_plan` y ajoute « et le contrôle est encore
 * attendu » (`APPOINTMENT_EXPECTED_STATUSES`) : c'est ce que demande le **rail** de l'écran
 * Planning, à qui un dossier archivé n'a rien à proposer. Confondre les deux casserait la
 * partition des compteurs ; n'offrir que le prédicat pur fait afficher des dossiers clos.
 */
export const INQUIRY_LIST_APPOINTMENTS = ['all', 'scheduled', 'unscheduled', 'to_plan'];
/**
 * Les statuts où **un rendez-vous reste à poser** : le contrôle est encore attendu.
 *
 * ⚠️ `submitted`, `validated`, `archived` et `dropped` en sont exclus, et c'est tout l'objet de
 * cette liste. Sans elle, le rail « À planifier » de l'écran Planning affiche des dossiers **clos**
 * — constaté à l'écran sur le jeu de démonstration, où il proposait de planifier des demandes
 * archivées. Personne ne leur posera de créneau.
 *
 * ⚠️ **Même ensemble que `REMINDER_MISSED_APPOINTMENT_STATUSES`, et pourtant une seconde
 * déclaration** : celui-là dit « un rendez-vous **passé** fait défaut », celui-ci « aucun
 * rendez-vous n'a jamais été posé ». Les deux questions peuvent diverger — le jour où un contrôle
 * transmis pourra être replanifié, l'une bougera sans l'autre. Les fondre ferait bouger les deux
 * écrans pour une décision qui n'en concerne qu'un.
 */
export const APPOINTMENT_EXPECTED_STATUSES = [
    'received',
    'assigned',
    'in_progress',
];
export const inquiryListAppointmentSchema = z.enum(INQUIRY_LIST_APPOINTMENTS);
/**
 * Ce que le serveur accepte pour construire une page : la **seule** définition dont dérivent
 * l'URL de l'écran, le `WHERE` du dépôt et l'appel du client typé (D24).
 *
 * ⚠️ Ce schéma est **strict** : il décrit ce que le service reçoit, pas ce qu'une URL contient.
 * La tolérance M21 — une valeur illisible retombe sur son défaut plutôt que de rendre `400` —
 * vit à l'entrée HTTP (`apps/api/src/inquiries/inquiry-list-query.ts`), au même endroit et de la
 * même façon que pour la tournée. Mélanger les deux ferait de `.catch()` une propriété du
 * modèle, alors que c'est une propriété de la frontière.
 *
 * `status` et `assignment` acceptent `'all'` plutôt que d'être facultatifs : « pas de filtre »
 * est une valeur du filtre, elle s'écrit dans l'URL et se lit dans un journal. Un champ absent
 * et un champ à `'all'` seraient deux façons de dire la même chose.
 */
export const inquiryListFilterSchema = z.object({
    /** Recherche libre — référence, dossier, rue, ville, nom d'équipe. Vide = pas de recherche. */
    search: z.string(),
    status: z.union([z.literal('all'), inquiryStatusSchema]),
    /**
     * Le filtre « Équipe » porte sur l'**identifiant**, jamais sur le nom : `JL·TB` est un champ
     * libre que renommer l'équipe change, et comparer des libellés ferait disparaître la moitié des
     * lignes au premier renommage (M22).
     *
     * ⚠️ Cumulé avec `assignment`, il le rend redondant sans le contredire : une demande affectée à
     * une équipe est affectée. C'est `assignment` qui reste utile seul — « non attribuées » ne
     * désigne aucune équipe.
     */
    teamId: z.union([z.literal('all'), uuidV7Schema]),
    /**
     * Le **donneur d'ordre**, par son identifiant — jamais par son nom (M32) : deux clients
     * peuvent porter des raisons sociales proches, et filtrer sur une chaîne ferait des rapproche-
     * ments que personne n'a demandés.
     *
     * ⚠️ **Il vise les deux chemins**, comme la colonne : la demande d'abord (`Inquiry.client_id`),
     * la campagne du lieu en repli (`Site.lot_id → Lot.client_id`). Filtrer sur le seul premier
     * ferait disparaître du tableau les demandes d'une campagne, qui sont pourtant bien de ce
     * client-là.
     */
    clientId: z.union([z.literal('all'), uuidV7Schema]),
    /**
     * Le **lot** — la campagne du lieu (`Site.lot_id`), le dernier filtre du rail « À planifier »
     * (#459). Par identifiant, comme le client.
     */
    lotId: z.union([z.literal('all'), uuidV7Schema]).default('all'),
    /** La **famille** du donneur d'ordre — les trois du prototype, pas les cinq catégories. */
    clientFamily: z.union([z.literal('all'), clientFamilySchema]),
    /**
     * Le **type de contrôle**. Champ libre côté modèle (M2, M13), donc une chaîne ici : la liste
     * de l'écran propose les six valeurs suggérées, et une septième ajoutée par GHP reste
     * filtrable par l'URL.
     */
    purpose: z.union([z.literal('all'), z.string().min(1)]),
    assignment: inquiryListAssignmentSchema,
    /**
     * La charge d'équipe — « à faire » / « réalisés » (cf. `INQUIRY_LIST_LOADS`). Sa place ici, à
     * côté de `teamId`, est ce qui rend la fiche équipe possible : les deux onglets sont **le même
     * filtre** à deux valeurs, comptés et paginés en base (D24).
     */
    load: inquiryListLoadSchema,
    /**
     * La présence d'un rendez-vous (cf. `INQUIRY_LIST_APPOINTMENTS`). C'est ce filtre que le rail
     * « À planifier » de l'écran Planning demande en `unscheduled` — il n'a donc pas de route à
     * lui : le rail *est* le tableau des demandes, vu sous un filtre.
     */
    appointment: inquiryListAppointmentSchema,
    /**
     * `started` : seulement les demandes dont la tablette a déjà remonté une étape (M42) —
     * l'onglet « En cours » de « Contrôles », avec `load=pending`. C'est un `EXISTS`.
     */
    progress: z.enum(INQUIRY_LIST_PROGRESSES).default('all'),
    sort: inquiryListSortSchema,
    direction: inquiryListDirectionSchema,
    limit: z.number().int().positive(),
    offset: z.number().int().min(0),
});
/**
 * Le plus de demandes cochées qu'un export accepte (#587) : une page du tableau à sa taille
 * maximale côté serveur (`MAX_PAGE_SIZE`). La sélection est celle de la page affichée, comme pour
 * « Attribuer » et « Planifier » — au-delà, ce n'est plus une sélection.
 */
export const MAX_INQUIRY_EXPORT_SELECTION = 200;
/**
 * Les demandes cochées qu'un export se borne à rendre (#587) : **une liste d'identifiants**, comme
 * l'export client (M14 § 5) — ce que l'utilisateur a coché est ce sur quoi il agit.
 */
export const inquiryExportSelectionSchema = z
    .array(uuidV7Schema)
    .min(1)
    .max(MAX_INQUIRY_EXPORT_SELECTION);
/**
 * Ce que l'export du tableau reçoit : les filtres de la liste **sans sa page**, et la sélection
 * cochée quand il y en a une.
 *
 * ⚠️ **La sélection remplace les filtres, elle ne s'y ajoute pas.** Rejouer le filtre écarterait
 * en silence une demande cochée qui en est sortie entre l'affichage et le clic — une demande
 * affectée pendant qu'on exporte la vue « Non attribuées ». Le tri, lui, s'applique toujours : le
 * fichier se lit dans l'ordre du tableau.
 */
export const inquiryListExportFilterSchema = inquiryListFilterSchema
    .omit({ limit: true, offset: true })
    .extend({ ids: inquiryExportSelectionSchema.optional() });
/**
 * Les effectifs d'onglets, comptés sur l'ensemble filtré — **jamais sur la page affichée** (D24,
 * M14 § 4). Un compteur d'onglet répond « combien si je clique ici » : il porte donc les filtres
 * en cours mais pas le préréglage de l'onglet, sinon il rendrait toujours le total de sa propre
 * vue.
 *
 * ⚠️ **Les quatre vues du prototype y sont désormais, et les deux dernières sont arrivées en
 * dernier.** « À planifier » et « Avec RDV » testent la présence d'un rendez-vous (M14) : elles
 * sont restées sans compteur tant qu'`Appointment` n'était pas en base, parce que les servir à
 * `0` ou à `total` aurait été inventer une réponse. La table existe depuis
 * `0006_person-appointment.sql` (M24) et l'écran Planning avait besoin du filtre : ils se
 * comptent maintenant comme les deux autres, en base.
 *
 * ⚠️ `scheduled` et `unscheduled` **partitionnent** l'ensemble recherché — leur somme vaut `all`.
 * C'est ce qui les distingue de `unassigned`, qui n'est qu'un sous-ensemble : une demande est
 * affectée ou non *et* planifiée ou non, les deux questions sont indépendantes.
 *
 * ⚠️ **Chaque onglet a son compteur, au prédicat exact de sa vue** : « Toutes » lit `all`, « Non
 * attribuées » `unassigned`, « Avec RDV » `scheduled` (la vue `appointment=scheduled`) et
 * « À planifier » `toPlan` (la vue `appointment=to_plan`) — **pas** `unscheduled`, qui compte
 * aussi les dossiers clos sans créneau que l'onglet écarte.
 */
export const inquiryListCountsSchema = z.object({
    all: z.number().int().min(0),
    unassigned: z.number().int().min(0),
    scheduled: z.number().int().min(0),
    unscheduled: z.number().int().min(0),
    /** Sans rendez-vous en cours **et** encore attendu (`APPOINTMENT_EXPECTED_STATUSES`). */
    toPlan: z.number().int().min(0),
});
/**
 * L'enveloppe paginée du back-office (D24) : la tranche demandée, l'effectif de l'ensemble
 * filtré, et les compteurs d'onglets.
 *
 * ⚠️ `total` et `items.length` diffèrent dès la première page — c'est `total` qui dit « 12
 * demandes » sous un tableau qui en montre cinquante au plus, et lui seul qui permet de savoir
 * s'il reste une page à demander. Les confondre est l'erreur que ce contrat existe pour
 * empêcher.
 *
 * `total` porte le préréglage de l'onglet, `counts` l'ignore : ils coïncident sur « Toutes » et
 * divergent ailleurs, d'où les deux.
 */
export const inquiryListPageSchema = z.object({
    items: z.array(inquiryListRowSchema),
    total: z.number().int().min(0),
    counts: inquiryListCountsSchema,
});
//# sourceMappingURL=inquiry-list.js.map